import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * OUTGLOW fix B07 (21/10/2026): once pinned, the summary is only unpinned above this ratio while the
   * drawer stays open (resize, Discount/note accordion, morph). Opening Discount adds ~60 px; with a single
   * 0.5 cutoff the summary would jump from the bottom to after the items while the shopper types the code.
   * @type {number}
   */
  #summaryReleaseThreshold = 0.65;

  /** OUTGLOW fix B07 (21/10/2026): watches the drawer and summary sizes to recompute the sticky state. */
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {Element | null} */
  #observedSummary = null;

  /** @type {number} */
  #resizeFrame = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // OUTGLOW fix B07 (21/10/2026): o Horizon só media o resumo ao abrir a gaveta e após um update do
    // carrinho. Agora também recalcula quando a tela muda de altura (barras do Safari, rotação), quando o
    // resumo cresce ou encolhe (Discount/nota abertos) e quando o morph troca o conteúdo da gaveta.
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleStickyUpdate);
      const dialog = this.#dialog;
      if (dialog) this.#resizeObserver.observe(dialog);
    }
    this.#mutationObserver = new MutationObserver(this.#scheduleStickyUpdate);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    // OUTGLOW fix B07 (21/10/2026)
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mutationObserver = null;
    this.#observedSummary = null;
    cancelAnimationFrame(this.#resizeFrame);
  }

  /** OUTGLOW fix B07 (21/10/2026): one recompute per frame, keeping a pinned summary pinned up to the release ratio. */
  #scheduleStickyUpdate = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => this.#updateStickyState({ keepPinned: true }));
  };

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // OUTGLOW fix B03 (21/10/2026): o Horizon abria a gaveta aqui, antes da resposta do /cart/add. Com 429
    // ou sem rede a gaveta abria com os itens antigos e parecia que o produto tinha entrado. Agora ela abre
    // só quando a promise do evento resolve sem erro (openAndSettle abaixo). Sem promise (evento de outro
    // script), mantém a abertura imediata original.
    if (shouldAutoOpen && !sourceModal && !event.promise && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState({ keepPinned: true }));

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * @param {{ keepPinned?: boolean }} [options] - OUTGLOW fix B07 (21/10/2026): when true, an already pinned
   *   summary stays pinned up to #summaryReleaseThreshold (used while the drawer is open). Opening the drawer
   *   keeps Horizon's single 0.5 cutoff.
   */
  #updateStickyState({ keepPinned = false } = {}) {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    // OUTGLOW fix B07 (21/10/2026): observe the current summary (the morph may replace the node).
    if (this.#resizeObserver && summary !== this.#observedSummary) {
      if (this.#observedSummary) this.#resizeObserver.unobserve(this.#observedSummary);
      if (summary) this.#resizeObserver.observe(summary);
      this.#observedSummary = summary;
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // OUTGLOW fix B07 (21/10/2026): closed drawer (display: none) measures 0; keep the last state until it opens.
    if (!drawerHeight) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const isPinned = dialog.getAttribute('cart-summary-sticky') !== 'false';
    const limit = keepPinned && isPinned ? this.#summaryReleaseThreshold : this.#summaryThreshold;
    dialog.setAttribute('cart-summary-sticky', ratio > limit ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
